import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { useCart } from '@/features/cart/context/CartContext';
import '../products.css';
import { fmt } from '@/lib/format';
import { resolveUnitPrice } from '@/lib/pricing';


const imgUrl = (url) => (url?.startsWith('/') ? `${url}` : url);

export default function Storefront() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q')?.trim() ?? '';
  const categoryId = searchParams.get('category_id') ?? '';

  const {
    addToCart,
    cart,
    updateQuantity,
    removeFromCart,
    toast,
    canShop,
  } = useCart();

  const getCartItem = (productId) => cart.find((item) => item.id === productId);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setProducts([]);

      try {
        const { data, error } = await apiClient.GET('/api/v1/products', {
          params: {
            query: {
              ...(searchQuery ? { q: searchQuery } : {}),
              ...(categoryId ? { category_id: categoryId } : {}),
            },
          },
        });
        if (!error && data) setProducts(data.items ?? []);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [searchQuery, categoryId]);

  if (loading) {
    return (
      <div className="container storefront-page" style={{ textAlign: 'center' }}>
        در حال بارگذاری محصولات...
      </div>
    );
  }

  return (
    <div className="container storefront-page">
      {toast && <div className="product-toast">✓ {toast}</div>}

      <h2 className="storefront-heading">
        {searchQuery
          ? `نتایج جستجو برای «${searchQuery}»`
          : categoryId
            ? 'محصولات این دسته‌بندی'
            : 'محصولات'}
      </h2>

      {products.length === 0 ? (
        <p className="storefront-empty">هیچ محصولی یافت نشد.</p>
      ) : (
        <div className="storefront-grid">
          {products.map((product) => {
            const cartItem = getCartItem(product.id);
            const categories = product.categories ?? [];
            const isOutOfStock = product.stock_quantity <= 0;

            return (
              <div key={product.id} className="product-tile">
                <Link to={`/product/${product.id}`} className="product-tile-image-link">
                  <div className="product-tile-image">
                    {product.image_url ? (
                      <img src={imgUrl(product.image_url)} alt={product.name_fa} loading="lazy" />
                    ) : (
                      <span className="product-tile-image-placeholder">بدون تصویر</span>
                    )}
                  </div>
                </Link>

                <div className="product-tile-body">
                  <Link to={`/product/${product.id}`} className="product-tile-title-link">
                    <h3 className="product-tile-title">{product.name_fa}</h3>
                  </Link>

                  {categories.length > 0 && (
                    <div className="product-tile-categories">
                      {categories.map((category) => (
                        <span key={category.id} className="product-tile-category">
                          {category.name_fa}
                        </span>
                      ))}
                    </div>
                  )}

                  <p className="product-tile-price">
                    {fmt(resolveUnitPrice(product, cartItem?.quantity ?? 1))} تومان
                  </p>

                  {product.wholesale_price != null && product.wholesale_min_quantity != null && (
                    <p className="product-tile-wholesale-hint">
                      عمده: {fmt(product.wholesale_price)} تومان (خرید {fmt(product.wholesale_min_quantity)} عدد یا بیشتر)
                    </p>
                  )}

                  <p className={`product-tile-stock ${isOutOfStock ? 'out-of-stock' : 'in-stock'}`}>
                    {isOutOfStock ? 'ناموجود' : `موجودی: ${fmt(product.stock_quantity)}`}
                  </p>

                  {!canShop ? (
                    <Link to={`/product/${product.id}`} className="product-tile-view">
                      <i className="fas fa-eye" />
                      مشاهده
                    </Link>
                  ) : cartItem ? (
                    <div className="product-tile-qty">
                      <button
                        className="product-tile-qty-btn"
                        onClick={() =>
                          cartItem.quantity <= 1
                            ? removeFromCart(product.id)
                            : updateQuantity(product.id, cartItem.quantity - 1)
                        }
                      >
                        −
                      </button>
                      <span className="product-tile-qty-value">{fmt(cartItem.quantity)}</span>
                      <button
                        className="product-tile-qty-btn"
                        disabled={cartItem.quantity >= product.stock_quantity}
                        onClick={() => updateQuantity(product.id, cartItem.quantity + 1)}
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    <button
                      className="product-tile-add"
                      disabled={isOutOfStock}
                      onClick={() => addToCart(product, 1)}
                    >
                      <i className="fas fa-cart-plus" />
                      {isOutOfStock ? 'ناموجود' : 'افزودن به سبد'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}